import React, { useState } from "react";
import { Avatar, Button, EmptyState, Icon, Input, Skeleton, fmtNum } from "../ui";
import { useAsync } from "../../hooks";
import content from "../../services/api-content";
import { useToast } from "../../context/ToastContext";
import ReportModal from "./ReportModal";
import "../../styles/content.css";

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
};

function Item({ c, replies, ownerId, meId, canManage, onReply, onDelete, onReport, depth = 0 }) {
  const toast = useToast();
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(Number(c.likeCount) || 0);
  const like = async () => {
    try {
      const r = await content.comments.like(c.id);
      setLiked(r.liked);
      setCount(r.likeCount ?? count + (r.liked ? 1 : -1));
    } catch (e) { toast(e.message, "error"); }
  };
  return (
    <div className="comment">
      <Avatar name={c.userName} src={c.userPhoto} size={34} />
      <div className="comment__body">
        <div className="row wrap" style={{ gap: 8 }}>
          <b>{c.userName || "ผู้ใช้"}</b>
          {c.userId === ownerId ? <span className="pill pill--ok">ครีเอเตอร์</span> : null}
          {c.isPin ? <span className="pill">ปักหมุด</span> : null}
          <span className="faint" style={{ fontSize: 12 }}>{when(c.createdAt)}</span>
        </div>
        <div className="comment__text">{c.text}</div>
        <div className="row" style={{ gap: 2, marginLeft: -8 }}>
          <button type="button" className={`act${liked ? " is-on" : ""}`} aria-pressed={liked} onClick={like}><Icon name="heart" size={15} filled={liked} /> {fmtNum(count)}</button>
          {depth === 0 ? <button type="button" className="act" onClick={() => onReply(c)}>ตอบกลับ</button> : null}
          {(c.userId === meId || canManage) ? <button type="button" className="act" onClick={() => onDelete(c)}>ลบ</button> : <button type="button" className="act" onClick={() => onReport(c)}>รายงาน</button>}
        </div>
        {replies.length ? <div className="replies">{replies.map((r) => <Item key={r.id} c={r} replies={[]} ownerId={ownerId} meId={meId} canManage={canManage} onReply={onReply} onDelete={onDelete} onReport={onReport} depth={1} />)}</div> : null}
      </div>
    </div>
  );
}

/** ความคิดเห็นของ character | universe | post */
export default function Comments({ targetType, targetId, ownerId, meId, isSuper, onCount }) {
  const toast = useToast();
  const [sort, setSort] = useState("new");
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const { data, loading, error, reload } = useAsync(() => content.comments.list(targetType, targetId, sort).then((rows) => { onCount?.(rows.filter((r) => !r.isDeleted).length); return rows; }), [targetType, targetId, sort]);

  const rows = (data || []).filter((r) => !r.isDeleted);
  const roots = rows.filter((r) => !r.parentId);
  const canManage = isSuper || ownerId === meId;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await content.comments.add(targetType, targetId, text.trim(), replyTo?.id);
      setText(""); setReplyTo(null);
      reload();
    } catch (err) { toast(err.message, "error"); } finally { setBusy(false); }
  };
  const del = async (c) => {
    if (!window.confirm("ลบความคิดเห็นนี้?")) return;
    try { await content.comments.remove(c.id); reload(); } catch (err) { toast(err.message, "error"); }
  };

  return (
    <div>
      <form onSubmit={submit}>
        {replyTo ? <div className="row-between muted" style={{ fontSize: 13, marginBottom: 6 }}>ตอบกลับ {replyTo.userName}<Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>ยกเลิก</Button></div> : null}
        <Input multiline maxLength={1000} placeholder="แสดงความคิดเห็น..." aria-label="ความคิดเห็น" value={text} onChange={(e) => setText(e.target.value)} hint={`${text.length}/1000`} style={{ minHeight: 70 }} />
        <div className="row-between"><div className="chip-row">
          {[["new", "ใหม่สุด"], ["top", "ยอดนิยม"]].map(([v, l]) => <button key={v} type="button" className={`chip chip--sm${sort === v ? " is-active" : ""}`} onClick={() => setSort(v)}>{l}</button>)}
        </div><Button type="submit" variant="primary" size="sm" disabled={busy || !text.trim()}>ส่ง</Button></div>
      </form>
      <div className="mt-16">
        {error ? <div className="error-text">{error}</div> : loading ? <Skeleton height={80} /> : !roots.length ? <EmptyState icon="💬" title="ยังไม่มีความคิดเห็น">เป็นคนแรกที่แสดงความเห็นสิ</EmptyState> : roots.map((c) => (
          <Item key={c.id} c={c} replies={rows.filter((r) => r.parentId === c.id)} ownerId={ownerId} meId={meId} canManage={canManage} onReply={setReplyTo} onDelete={del} onReport={(x) => setReport(x)} />
        ))}
      </div>
      <ReportModal open={!!report} onClose={() => setReport(null)} targetType="comment" targetId={report?.id} title="รายงานความคิดเห็น" />
    </div>
  );
}
